// src/components/HotelSearchResults.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchHotelOffers, fetchHotelOffersDetails } from "../../../utils/Hote";
import { fetchTripadvisorMatch, clearTripAdvisorCache, getTripAdvisorCacheStats } from "../../../utils/tripadvisor";
import { NoResultsFoundWithButtons } from "../../../components/common/NoResultsFoundWithButton";
import FiltersSidebar from "./FiltersSideBar";
import HotelMapModal from "./HotelMapSearch";
import { DrawerComponent } from "../../../components/common/Drawer";
import BookmarkScoreIcon from "../../../assets/svg/GreenBookmarkIcon";
import { DarkMoonIcon } from "../../../assets/svg/DarkMoon";
import starIcon from "../../../assets/img/star.png";
import tripAdvisorLogo from "../../../assets/img/TripAdvisorLogo.svg";
import mapIcon from "../../../assets/img/icon-map.png";
import { Sort as SortIcon } from "@mui/icons-material";
import { addDistanceToHotels } from "../../../utils/geoUtils";
import { Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { 
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  Restaurant as RestaurantIcon,
  Pool as PoolIcon,
  FitnessCenter as GymIcon,
  Spa as SpaIcon,
  AcUnit as AcIcon,
  Room as RoomServiceIcon,
  LocalBar as BarIcon,
  Business as BusinessIcon,
  Euro as PaymentIcon,
  LocationOn as LocationIcon,
  PersonOutline as PersonOutlineIcon,
  AccountBalanceWallet as WalletIcon,
  Bed as BedIcon
} from '@mui/icons-material';
// Import cutlery SVG from svg2 folder as inline component
const CutlerySVG = ({ style }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 100 100" 
    style={style}
  >
    <path d="M69.215 43.508c6.382.55 13.919-2.365 19.884-8.33 9.15-9.15 11.133-22.002 4.429-28.706-6.704-6.703-19.556-4.72-28.706 4.429-5.965 5.965-8.88 13.502-8.33 19.884a13.94 13.94 0 0 1-2.823 9.695 60.045 60.045 0 0 1-3.632 4.265 47.135 47.135 0 0 1-2.676-3.587 5.898 5.898 0 0 1-.698-4.925 13.96 13.96 0 0 0-3.555-13.7C36.67 16.097 26.042 6.402 22.29 3a1.93 1.93 0 0 0-2.664.07l-.067.066a1.936 1.936 0 0 0 0 2.737l18.735 18.735a1.937 1.937 0 0 1-.248 2.949c-.81.538-1.89.419-2.563-.284L16.823 8.61a1.937 1.937 0 0 0-2.949.248c-.538.81-.418 1.89.284 2.562L32.82 30.083a1.937 1.937 0 0 1-.248 2.949c-.81.538-1.89.418-2.562-.285L11.348 14.085a1.937 1.937 0 0 0-2.948.249c-.539.81-.419 1.89.284 2.562l18.662 18.662a1.937 1.937 0 0 1-.249 2.948c-.81.539-1.89.419-2.562-.284L5.873 19.56a1.936 1.936 0 0 0-2.737 0l-.067.067A1.93 1.93 0 0 0 3 22.29c3.403 3.751 13.098 14.38 19.534 20.817a13.96 13.96 0 0 0 13.7 3.555 5.897 5.897 0 0 1 4.925.698 47.128 47.128 0 0 1 3.582 2.673C37.6 56.75 26.224 66.252 6.47 82.162c-5.449 4.389-4.676 9.408-1.358 12.726 3.318 3.318 8.337 4.09 12.726-1.358C42.94 62.364 52.098 52.036 59.52 46.33a13.94 13.94 0 0 1 9.695-2.823z" fill="currentColor" />
    <path d="M93.742 82.984C75.621 68.388 64.952 59.494 58.16 53.17c-1.492 1.386-3.129 3.021-4.985 4.991 6.322 6.793 15.216 17.461 29.81 35.58 4.153 5.157 8.903 4.426 12.044 1.286 3.14-3.14 3.871-7.89-1.286-12.044z" fill="currentColor" />
  </svg>
);

// Helpers
const getScoreLabel = (score, translation) => {
  if (score >= 90) return translation("excellent");
  if (score >= 75) return translation("very_good");
  if (score >= 60) return translation("good");
  return translation("average");
};

const formatDateWithSuffix = (dateStr) => {
  try {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return ""; // Kontrolli për data të pavlefshme
    
    const day = d.getDate();
    const month = d.toLocaleString("default", { month: "long" });
    const suffix = day > 3 && day < 21
      ? "th"
      : ["st", "nd", "rd"][day % 10 - 1] || "th";
    return `${day}${suffix} ${month}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// Funksion për të shfaqur datën në formatin e duhur për shfaqjen e hoteleve
const formatDateForDisplay = (dateStr) => {
  try {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    
    // Format: "27th April" or similar
    const day = d.getDate();
    const month = d.toLocaleString("default", { month: "long" });
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  } catch (error) {
    console.error("Error formatting date for display:", error);
    return "";
  }
};

// Funksion ndihmës për të vendosur prapashtesën e duhur (1st, 2nd, 3rd, 4th...)
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

// Funksion për të gjeneruar datat kur nuk janë në parametrat e kërkimit
const getDefaultDates = () => {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  
  return {
    checkIn: today.toISOString().split('T')[0],
    checkOut: tomorrow.toISOString().split('T')[0]
  };
};

const Container = styled.div`
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: ${props => props.isMobile ? "100%" : "1400px"};
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    padding: 0;
    margin: 0;
    max-width: 100vw;
    width: 100vw;
    overflow-x: hidden;
  }
`;

const SpecialOfferBadge = styled.div`
  position: absolute;
  bottom: ${props => props.isMobile ? '130px' : '210px'};
  left: -6px;
  background-color: #FAD217;
  color: #003663;
  font-weight: bold;
  font-size: ${props => props.isMobile ? '14px' : '16px'};
  padding: 8px 20px 8px 16px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  z-index: 3;
  min-width: ${props => props.isMobile ? '140px' : '220px'};
  max-width: ${props => props.isMobile ? '180px' : '250px'};
  box-shadow: 0 2px 8px rgba(15, 54, 99, 0.3);
  overflow: visible;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    height: 6px;
    width: 100%;
    background-color: #003663;
    z-index: -1;
    border-bottom-right-radius: 6px;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-top: 6px solid #003663;
    z-index: 2;
  }
`;

const HotelCard = styled.div`
  border-radius: ${props => props.isMobile ? '12px' : '12px'};
  overflow: visible;
  box-shadow: ${props => props.isMobile ? '0 4px 12px rgba(0,0,0,0.08)' : '0 6px 16px rgba(0,0,0,0.15)'};
  cursor: pointer;
  margin: ${props => props.isMobile ? '16px 12px' : '0 0 24px 0'};
  width: ${props => props.isMobile ? 'calc(100% - 24px)' : '100%'};
  background-color: #fff;
  padding: 0;
  max-width: ${props => props.isMobile ? 'calc(100% - 24px)' : '100%'};
  flex: 1 1 auto;
  height: ${props => props.isMobile ? 'auto' : '300px'};
  display: flex;
  flex-direction: ${props => props.isMobile ? 'column' : 'row'};
  color: #003663;
  border: 1px solid #eee;

  @media (max-width: 768px) {
    margin: 12px;
    &:hover {
      transform: translateY(-2px);
      transition: transform 0.2s ease-in-out;
    }
  }
`;

const HotelImage = styled.div`
  flex: 0 0 ${props => props.isMobile ? 'auto' : '33.333%'};
  position: relative;
  padding: 0;
  height: ${props => props.isMobile ? '220px' : '100%'};
  width: ${props => props.isMobile ? '100vw' : '100%'};
  overflow: visible;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// Funksion ndihmës për të marrë simbolin e monedhës
const getCurrencySymbol = (currencyCode) => {
  const currencyMap = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'JPY': '¥',
    'TRY': '₺',
    'CHF': 'CHF',
    'AED': 'د.إ',
    'ALL': 'L'
  };
  
  return currencyMap[currencyCode] || currencyCode;
};

// Funksion për të marrë tekstin e duhur për llojin e ushqimit
const getMealTypeDisplay = (boardName, translation) => {
  if (!boardName) return translation("no_meals_included");
  
  const boardNameLower = boardName.toLowerCase();
  
  if (boardNameLower.includes("room only") || boardNameLower.includes("room_only")) {
    return translation("no_breakfast_included");
  } else if (boardNameLower.includes("bed and breakfast") || boardNameLower.includes("breakfast")) {
    return translation("breakfast_included");
  } else if (boardNameLower.includes("half board") || boardNameLower.includes("half_board")) {
    return translation("half_board");
  } else if (boardNameLower.includes("full board") || boardNameLower.includes("full_board")) {
    return translation("full_board");
  } else if (boardNameLower.includes("all inclusive") || boardNameLower.includes("all_inclusive")) {
    return translation("all_inclusive");
  } else {
    return boardName;
  }
};

const HotelSearchResults = ({ searchParams, translation }) => {
  const navigate = useNavigate();

  // Nëse datat në URL janë bosh, përdor vlera default
  // Memoizoj objektin effectiveParams për të parandaluar ri-renderimet e panevojshme
  const effectiveParams = useMemo(() => {
    const defaultDates = getDefaultDates();
    const params = {
      ...searchParams,
      checkInDate: searchParams.checkInDate || defaultDates.checkIn,
      checkOutDate: searchParams.checkOutDate || defaultDates.checkOut,
    };
    
    return params;
  }, [searchParams]);

  const [allHotels, setAllHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [searchId, setSearchId] = useState("");
  const [searchExpiresOn, setSearchExpiresOn] = useState(null); // ✅ Add search expiration tracking
  const [visibleHotels, setVisibleHotels] = useState(20);
  const [loading, setLoading] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tripAdvisorResponses, setTripAdvisorResponses] = useState({});
  const [tripAdvisorAttempted, setTripAdvisorAttempted] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [hoveredOffer, setHoveredOffer] = useState(null);
  const [hoverShowRooms, setHoverShowRooms] = useState(false);
  const [hoveredPriceId, setHoveredPriceId] = useState(null);
  
  // Opsionet e radhitjes
  const [sortOption, setSortOption] = useState("recommended"); // Opsioni fillestar i radhitjes
  
  // Add state for stable sorting and special offers
  const [sortedAndCachedHotels, setSortedAndCachedHotels] = useState([]);
  const [lastSortOption, setLastSortOption] = useState("recommended");
  const [specialOfferHotels, setSpecialOfferHotels] = useState(new Set());
  
  const sortOptions = [
    { id: "recommended", label: "Sort By", translationKey: "sort_by" },
    { id: "price_low_high", label: "Price (low to high)", translationKey: "price_low_to_high" },
    { id: "price_high_low", label: "Price (high to low)", translationKey: "price_high_to_low" },
    { id: "distance", label: "Distance from city center", translationKey: "distance_from_city_center" },
    { id: "rating_high_low", label: "Rating (high to low)", translationKey: "rating_high_to_low" },
    { id: "rating_low_high", label: "Rating (low to high)", translationKey: "rating_low_to_high" }
  ];
  
  const [filters, setFilters] = useState({
    starRating: [],
    distanceKm: [],
    mealPlan: [],
    priceMin: null,
    priceMax: null,
    facilities: [],
    hotelName: "",
    themes: [],
  });

  // Funksioni për mbylljen e dropdown kur klikohet diku tjetër
  const mobileSortDropdownRef = useRef(null);
  const desktopSortDropdownRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const desktopButtonRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both dropdown containers and not on any sort button
      const isClickOutsideMobile = mobileSortDropdownRef.current && !mobileSortDropdownRef.current.contains(event.target);
      const isClickOutsideDesktop = desktopSortDropdownRef.current && !desktopSortDropdownRef.current.contains(event.target);
      
      // Check if the click is not on mobile or desktop sort buttons
      const isClickOnMobileButton = mobileButtonRef.current && mobileButtonRef.current.contains(event.target);
      const isClickOnDesktopButton = desktopButtonRef.current && desktopButtonRef.current.contains(event.target);
      
      // Close dropdown if click is outside both containers and not on any sort button
      if (isClickOutsideMobile && isClickOutsideDesktop && !isClickOnMobileButton && !isClickOnDesktopButton) {
        setIsSortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Funksion për të trajtuar klikimin në butonin e sort-it
  const handleSortButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsSortDropdownOpen(prev => !prev);
  };
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Shto një state për roomCriteria
  const [roomCriteria, setRoomCriteria] = useState(null);

  // Kërkesa fillestar për hotele, vetëm një herë ose kur ndryshojnë parametrat kryesorë
  useEffect(() => {
    console.log('🔄 fetchHotels useEffect triggered with params:', effectiveParams);
    
    const fetchHotels = async () => {
      // Prevent multiple simultaneous calls
      if (loading) {
        console.log('🛑 fetchHotels already in progress, skipping...');
        return;
      }
      
      setLoading(true);
      try {
        // Kontrollojmë nëse kemi roomCriteria në session storage
        const storedRoomCriteria = sessionStorage.getItem('roomCriteria');
        let roomCriteriaParam = null;
        
        if (storedRoomCriteria) {
          try {
            roomCriteriaParam = JSON.parse(storedRoomCriteria);
            setRoomCriteria(roomCriteriaParam);
          } catch (e) {
            console.error("Error parsing roomCriteria from session storage:", e);
          }
        }

        const res = await fetchHotelOffers({
          cityId: effectiveParams.cityId,
          checkInDate: effectiveParams.checkInDate,
          checkOutDate: effectiveParams.checkOutDate,
          adults: effectiveParams.adults,
          children: effectiveParams.children,
          childAges: Array(effectiveParams.children).fill(5),
          currency: effectiveParams.currency,
          nationality: effectiveParams.nationality || "XK",
          roomCriteria: roomCriteriaParam
        });
        const hotels = res.hotels || res.body?.hotels || [];
        const sId = res.searchId || res.body?.searchId || "";
        const searchExpiry = res.expiresOn || res.body?.expiresOn;
        
        // Kjo është pjesa ku aplikojmë distancën nga qendra e qytetit për secilin hotel
        const cityLat = effectiveParams.cityLat;
        const cityLng = effectiveParams.cityLng;
        const hotelsWithDistance = addDistanceToHotels(hotels, cityLat, cityLng);
        
        const updated = hotelsWithDistance.map((h) => {
          const hotelId = h.id || h.giataInfo?.hotelId || h.offers?.[0]?.hotelId || "unknown";
          return {
            ...h,
            id: hotelId,
            displayPrice: +((h.offers?.[0]?.price?.amount || 0)).toFixed(2),
          };
        });
        
        console.log('📊 Hotels data structure sample:', updated.slice(0, 2).map(h => ({
          name: h.name,
          boardGroups: h.boardGroups,
          offers: h.offers?.slice(0, 1)
        })));
        
        setAllHotels(updated);
        setSearchId(sId);
        setSearchExpiresOn(searchExpiry); // ✅ Store search expiration
        
        // Clear TripAdvisor cache when new search is performed
        setTripAdvisorResponses({});
        setTripAdvisorAttempted(new Set());
        
        // Reset sorted cache when new hotels are loaded
        setSortedAndCachedHotels([]);
        setLastSortOption("");
        setSpecialOfferHotels(new Set());
      } catch (e) {
        console.error(e);
        setAllHotels([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (effectiveParams.cityId) {
      // Only fetch if we don't have a valid searchId or if the search has expired
      const shouldFetch = !searchId || (searchExpiresOn && new Date() > new Date(searchExpiresOn));
      
      if (shouldFetch) {
        console.log('🚀 Fetching hotels...');
        fetchHotels();
      } else {
        console.log('✅ Using existing search results, searchId:', searchId);
      }
    }
  }, [
    effectiveParams.cityId, 
    effectiveParams.checkInDate, 
    effectiveParams.checkOutDate,
    effectiveParams.adults,
    effectiveParams.children,
    effectiveParams.nationality,
    searchId,
    searchExpiresOn
  ]);



  const debounceRef = useRef();
  const [hasScrolledToResults, setHasScrolledToResults] = useState(false);
  
  // Add state for price updates when meal plan filters are applied
  const [priceUpdateInProgress, setPriceUpdateInProgress] = useState(false);
  const [updatedPrices, setUpdatedPrices] = useState({});
  const [lastMealPlanFilter, setLastMealPlanFilter] = useState([]);
  const [hotelsBeingUpdated, setHotelsBeingUpdated] = useState(0);
  const [hotelsProcessed, setHotelsProcessed] = useState(0);

  // Enhanced sorting function with special offers handling
  const sortHotels = useCallback((hotels, sortBy, tripAdvisorData = tripAdvisorResponses) => {
    if (!hotels || !hotels.length) return [];
    
    const hotelsCopy = [...hotels];
    
    // Helper function to get hotel score
    const getHotelScore = (hotel) => {
      const ta = tripAdvisorData[hotel.id]?.hotels?.[0] || {};
      const rawScore = ta.averageRating
        ? ta.averageRating * 20
        : (hotel.rating || 0) * 20;
      return Math.min(Math.round(rawScore), 100);
    };
    
    // Helper function to get display price (updated or original)
    const getDisplayPrice = (hotel) => {
      if (updatedPrices[hotel.id]) {
        return updatedPrices[hotel.id].price;
      }
      return hotel.displayPrice;
    };
    
    // Separate hotels with 0 or less rating from those with valid ratings
    const hotelsWithValidRatings = hotelsCopy.filter(hotel => getHotelScore(hotel) > 0);
    const hotelsWithZeroRatings = hotelsCopy.filter(hotel => getHotelScore(hotel) <= 0);
    
    // Identify special offers (3-4 lowest price hotels within 2km of city center)
    const hotelsNearCenter = hotelsWithValidRatings.filter(h => 
      (h.distanceFromCenter || 999) <= 2 // Only hotels within 2km
    );
    
    let specialOfferIds = new Set(); // Declare outside the if block
    
    if (hotelsNearCenter.length > 0) {
      const sortedByPrice = [...hotelsNearCenter].sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
      specialOfferIds = new Set(sortedByPrice.slice(0, 4).map(h => h.id));
    } else {
      // Fallback: if no hotels within 2km, don't mark any as special offers
      specialOfferIds = new Set();
    }
    
    setSpecialOfferHotels(specialOfferIds);
    
    let sortedValidRatingHotels;
    let sortedZeroRatingHotels;
    
    switch (sortBy) {
      case "price_low_high":
        sortedValidRatingHotels = hotelsWithValidRatings.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        sortedZeroRatingHotels = hotelsWithZeroRatings.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
      
      case "price_high_low":
        sortedValidRatingHotels = hotelsWithValidRatings.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
        sortedZeroRatingHotels = hotelsWithZeroRatings.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
        break;
      
      case "distance":
        sortedValidRatingHotels = hotelsWithValidRatings.sort((a, b) => (a.distanceFromCenter || 999) - (b.distanceFromCenter || 999));
        sortedZeroRatingHotels = hotelsWithZeroRatings.sort((a, b) => (a.distanceFromCenter || 999) - (b.distanceFromCenter || 999));
        break;
      
      case "rating_high_low":
        sortedValidRatingHotels = hotelsWithValidRatings.sort((a, b) => {
          const scoreA = tripAdvisorData[a.id]?.hotels?.[0]?.averageRating * 20 || a.rating * 20 || 0;
          const scoreB = tripAdvisorData[b.id]?.hotels?.[0]?.averageRating * 20 || b.rating * 20 || 0;
          return scoreB - scoreA;
        });
        sortedZeroRatingHotels = hotelsWithZeroRatings.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
      
      case "rating_low_high":
        sortedValidRatingHotels = hotelsWithValidRatings.sort((a, b) => {
          const scoreA = tripAdvisorData[a.id]?.hotels?.[0]?.averageRating * 20 || a.rating * 20 || 0;
          const scoreB = tripAdvisorData[b.id]?.hotels?.[0]?.averageRating * 20 || b.rating * 20 || 0;
          return scoreA - scoreB;
        });
        sortedZeroRatingHotels = hotelsWithZeroRatings.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
      
      case "recommended":
      default:
        // For recommended sorting, prioritize special offers at the top
        const specialOffers = hotelsWithValidRatings.filter(h => specialOfferIds.has(h.id));
        const regularOffers = hotelsWithValidRatings.filter(h => !specialOfferIds.has(h.id));
        
        // Sort special offers by price (lowest first)
        specialOffers.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        
        // Sort regular offers by recommendation algorithm
        regularOffers.sort((a, b) => {
          const scoreA = tripAdvisorData[a.id]?.hotels?.[0]?.averageRating * 20 || a.rating * 20 || 0;
          const scoreB = tripAdvisorData[b.id]?.hotels?.[0]?.averageRating * 20 || b.rating * 20 || 0;
          
          const ratingWeight = 0.5;
          const priceWeight = 0.3;
          const distanceWeight = 0.2;
          
          const aRatingScore = scoreA / 100;
          const bRatingScore = scoreB / 100;
          
          const maxPrice = Math.max(...hotelsWithValidRatings.map(h => getDisplayPrice(h)));
          const aPriceScore = maxPrice ? 1 - (getDisplayPrice(a) / maxPrice) : 0;
          const bPriceScore = maxPrice ? 1 - (getDisplayPrice(b) / maxPrice) : 0;
          
          const maxDistance = Math.max(...hotelsWithValidRatings.map(h => h.distanceFromCenter || 0));
          const aDistanceScore = maxDistance ? 1 - ((a.distanceFromCenter || 0) / maxDistance) : 0;
          const bDistanceScore = maxDistance ? 1 - ((b.distanceFromCenter || 0) / maxDistance) : 0;
          
          const aTotalScore = (aRatingScore * ratingWeight) + (aPriceScore * priceWeight) + (aDistanceScore * distanceWeight);
          const bTotalScore = (bRatingScore * ratingWeight) + (bPriceScore * priceWeight) + (bDistanceScore * distanceWeight);
          
          return bTotalScore - aTotalScore;
        });
        
        sortedValidRatingHotels = [...specialOffers, ...regularOffers];
        sortedZeroRatingHotels = hotelsWithZeroRatings.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
    }
    
    // Combine sorted hotels with valid ratings first, then zero ratings at the end
    return [...sortedValidRatingHotels, ...sortedZeroRatingHotels];
  }, [updatedPrices, tripAdvisorResponses]);

  // Function to update hotel prices based on selected meal plan
  const updateHotelPricesForMealPlan = async (hotels, selectedMealPlans) => {
    if (!selectedMealPlans || selectedMealPlans.length === 0) {
      setUpdatedPrices({});
      return;
    }

    // Prevent multiple simultaneous calls
    if (priceUpdateInProgress) {
      console.log('🛑 Price update already in progress, skipping...');
      return;
    }

    setPriceUpdateInProgress(true);
    setHotelsBeingUpdated(hotels.length);
    setHotelsProcessed(0);
    const newPrices = {};
    
    console.log(`🔄 Updating prices for ${hotels.length} hotels with board type: ${selectedMealPlans.join(', ')}`);

    try {
      // Process hotels in batches to avoid overwhelming the API
      // Use smaller batches for fewer hotels, larger batches for more hotels
      const batchSize = hotels.length <= 10 ? 3 : hotels.length <= 20 ? 5 : 8;
      for (let i = 0; i < hotels.length; i += batchSize) {
        const batch = hotels.slice(i, i + batchSize);
        
        const promises = batch.map(async (hotel) => {
          try {
            // Get the correct offerId - it should be the complex format from the offers array
            const offerId = hotel.offers?.[0]?.offerId;
            
            // Skip if no valid offerId is available
            if (!offerId) {
              console.warn(`⚠️ No valid offerId found for hotel ${hotel.id}, skipping price update`);
              return;
            }
            
            console.log(`🔍 Fetching room details for hotel ${hotel.id} with offerId: ${offerId}`);
            
            // Get room details for this hotel
            const result = await fetchHotelOffersDetails({
              searchId: searchId,
              offerId: offerId,
              productType: 2, // hotels
              productId: hotel.id,
              currency: effectiveParams.currency || "EUR",
              culture: "en-US",
              getRoomInfo: true
            });

            if (result?.offers) {
              // Find the cheapest room that matches the selected meal plan
              let cheapestPrice = Infinity;
              let cheapestRoom = null;

              result.offers.forEach(offer => {
                offer.rooms?.forEach(room => {
                  room.boardGroups?.forEach(boardGroup => {
                    if (selectedMealPlans.includes(boardGroup.name)) {
                      const roomPrice = offer.price?.amount || 0;
                      if (roomPrice < cheapestPrice) {
                        cheapestPrice = roomPrice;
                        cheapestRoom = {
                          price: roomPrice,
                          boardName: boardGroup.name,
                          roomName: room.name
                        };
                      }
                    }
                  });
                });
              });

              if (cheapestRoom) {
                newPrices[hotel.id] = {
                  price: cheapestRoom.price,
                  boardName: cheapestRoom.boardName,
                  roomName: cheapestRoom.roomName
                };
              }
            }
          } catch (error) {
            console.error(`Error updating price for hotel ${hotel.id}:`, error);
            // Keep original price if API call fails
            newPrices[hotel.id] = {
              price: hotel.displayPrice,
              boardName: hotel.boardGroups?.[0]?.name || "Unknown",
              roomName: hotel.offers?.[0]?.roomName || "Unknown"
            };
          }
        });

        await Promise.all(promises);
        setHotelsProcessed(prev => prev + batch.length);
      }

      setUpdatedPrices(newPrices);
    } catch (error) {
      console.error("Error updating hotel prices:", error);
    } finally {
      setPriceUpdateInProgress(false);
      setHotelsBeingUpdated(0);
      setHotelsProcessed(0);
    }
  };

  // Enhanced filtering logic with price updates
  useEffect(() => {
    clearTimeout(debounceRef.current);
    
    debounceRef.current = setTimeout(() => {
      let list = [...allHotels];
      
      console.log('🔍 Starting filtering process...');
      console.log('📊 Total hotels:', allHotels.length);
      console.log('🎯 Current filters:', filters);
      
      // Helper function to get display price (updated or original)
      const getDisplayPrice = (hotel) => {
        if (updatedPrices[hotel.id]) {
          return updatedPrices[hotel.id].price;
        }
        return hotel.displayPrice;
      };
      
      // Apply filters
      if (filters.hotelName)
        list = list.filter((h) =>
          h.name.toLowerCase().includes(filters.hotelName.toLowerCase())
        );
      
      if (filters.priceMin != null)
        list = list.filter((h) => getDisplayPrice(h) >= filters.priceMin);
      
      if (filters.priceMax != null)
        list = list.filter((h) => getDisplayPrice(h) <= filters.priceMax);
      
      // Distance filter - only apply if a specific distance is selected
      if (filters.distanceKm && filters.distanceKm.length)
        list = list.filter((h) => h.distanceFromCenter <= Math.min(...filters.distanceKm));
      
      // Star rating filter
      if (filters.starRating.length)
        list = list.filter((h) =>
          filters.starRating.some((r) => Math.round(h.stars || 0) >= r)
        );
      
      // Meal plan filter - only apply if a specific meal plan is selected
      if (filters.mealPlan && filters.mealPlan.length) {
        console.log('🍽️ Before meal plan filter:', list.length, 'hotels');
        
        // Check both top-level boardGroups and offers-level boardGroups
        list = list.filter((h) => {
          // Check top-level boardGroups
          const hasTopLevelBoard = h.boardGroups?.some((bg) => filters.mealPlan.includes(bg.name));
          
          // Check offers-level boardGroups
          const hasOffersBoard = h.offers?.some(offer => 
            offer.rooms?.some(room => 
              room.boardGroups?.some((bg) => filters.mealPlan.includes(bg.name))
            )
          );
          
          return hasTopLevelBoard || hasOffersBoard;
        });
        
        console.log('🍽️ After meal plan filter:', list.length, 'hotels');
        
        // Debug: Log some hotel board groups
        if (list.length === 0 && allHotels.length > 0) {
          console.log('🔍 Debug: Sample hotel board groups:');
          allHotels.slice(0, 3).forEach((hotel, index) => {
            console.log(`Hotel ${index + 1}:`, hotel.name, 'Board groups:', hotel.boardGroups);
            console.log(`Hotel ${index + 1} offers:`, hotel.offers?.slice(0, 1));
          });
        }
      }
      
      // Facilities filter
      if (filters.facilities && filters.facilities.length)
        list = list.filter((h) =>
          filters.facilities.every((f) =>
            h.facilities?.map((x) => x.name).includes(f)
          )
        );

      // Themes filter
      if (filters.themes && filters.themes.length)
        list = list.filter((h) =>
          filters.themes.every((t) =>
            h.themes?.map((x) => x.name).includes(t)
          )
        );
        
      console.log('✅ Final filtered hotels:', list.length);
      console.log('🎯 Final list:', list.slice(0, 3).map(h => ({ name: h.name, boardGroups: h.boardGroups })));
      
      // Apply sorting
      const sortedList = sortHotels(list, sortOption, tripAdvisorResponses);
      setFilteredHotels(sortedList);
      
      // Cache the sorted results
      setSortedAndCachedHotels(sortedList);
      
      // Scroll to results on mobile when they first load
      if (isMobile && list.length > 0 && !hasScrolledToResults && !loading) {
        setTimeout(() => {
          const resultsSection = document.querySelector('.hotel-results-section');
          if (resultsSection) {
            const offsetTop = resultsSection.offsetTop - 100; // 100px offset from top
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });
            setHasScrolledToResults(true);
          }
        }, 300); // Small delay to ensure content is rendered
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [allHotels, filters, sortOption, lastSortOption, sortedAndCachedHotels, updatedPrices, isMobile, hasScrolledToResults, loading, searchId, lastMealPlanFilter]);

  // Separate useEffect to handle meal plan price updates
  const lastMealPlanFilterRef = useRef([]);
  
  useEffect(() => {
    // Only trigger price updates when meal plan filter changes
    if (filters.mealPlan && filters.mealPlan.length > 0 && searchId) {
      // Check if the meal plan filter has actually changed
      const currentMealPlan = JSON.stringify(filters.mealPlan.sort());
      const lastMealPlan = JSON.stringify(lastMealPlanFilterRef.current.sort());
      
      if (currentMealPlan !== lastMealPlan) {
        console.log('🔄 Meal plan filter changed:', filters.mealPlan);
        
        // Get the filtered hotels that match the meal plan
        const filteredHotelsForMealPlan = allHotels.filter(h => {
          // Check both top-level boardGroups and offers-level boardGroups
          const hasTopLevelBoard = h.boardGroups?.some((bg) => filters.mealPlan.includes(bg.name));
          
          // Check offers-level boardGroups
          const hasOffersBoard = h.offers?.some(offer => 
            offer.rooms?.some(room => 
              room.boardGroups?.some((bg) => filters.mealPlan.includes(bg.name))
            )
          );
          
          return hasTopLevelBoard || hasOffersBoard;
        });
        
        console.log(`📊 Found ${filteredHotelsForMealPlan.length} hotels with ${filters.mealPlan.join(', ')} board type`);
        
        if (filteredHotelsForMealPlan.length > 0) {
          // Limit to first 20 hotels to avoid excessive API calls
          const hotelsToUpdate = filteredHotelsForMealPlan.slice(0, 20);
          console.log(`🚀 Starting price update for ${hotelsToUpdate.length} hotels`);
          updateHotelPricesForMealPlan(hotelsToUpdate, filters.mealPlan);
          lastMealPlanFilterRef.current = [...filters.mealPlan];
          setLastMealPlanFilter([...filters.mealPlan]);
        }
      }
    } else if (!filters.mealPlan || filters.mealPlan.length === 0) {
      // Only clear if we actually had a meal plan filter before
      if (lastMealPlanFilterRef.current.length > 0) {
        console.log('🧹 Clearing meal plan filter and updated prices');
        // Clear updated prices when no meal plan filter is applied
        setUpdatedPrices({});
        lastMealPlanFilterRef.current = [];
        setLastMealPlanFilter([]);
      }
    }
  }, [filters.mealPlan, searchId, allHotels]);

  // Add TripAdvisor cache utilities for debugging (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.tripAdvisorCache = {
        clear: clearTripAdvisorCache,
        stats: getTripAdvisorCacheStats,
        view: () => {
          try {
            return JSON.parse(localStorage.getItem('tripAdvisorCache') || '{}');
          } catch (e) {
            return {};
          }
        }
      };
    }
  }, []);

  // Add debounce for TripAdvisor calls
  const tripAdvisorDebounceRef = useRef();
  const prevFilteredHotelsRef = useRef([]);

  useEffect(() => {
    // Only trigger if filteredHotels actually changed meaningfully
    const currentHotelIds = filteredHotels.slice(0, visibleHotels).map(h => h.id).join(',');
    const prevHotelIds = prevFilteredHotelsRef.current.join(',');
    
    if (currentHotelIds === prevHotelIds) {
      return; // No meaningful change, skip
    }
    
    // Update the ref
    prevFilteredHotelsRef.current = filteredHotels.slice(0, visibleHotels).map(h => h.id);
    
    // Clear previous timeout
    clearTimeout(tripAdvisorDebounceRef.current);
    
    // Set a new timeout to debounce the TripAdvisor calls
    tripAdvisorDebounceRef.current = setTimeout(() => {
      const fetchTripAdvisorData = async () => {
        // Only fetch TripAdvisor data for hotels that are actually being displayed
        // Process in chunks of 20 to match the pagination
        const currentlyDisplayedHotels = filteredHotels.slice(0, visibleHotels);
        
        // Find hotels that need TripAdvisor data and group them in batches of 20
        const hotelsNeedingData = currentlyDisplayedHotels.filter(hotel => 
          hotel.id && 
          !tripAdvisorResponses[hotel.id] && 
          !tripAdvisorAttempted.has(hotel.id)
        );
        
        if (hotelsNeedingData.length === 0) {
          return;
        }
        
        // Process only the first 20 hotels that need data
        const hotelsToFetch = hotelsNeedingData.slice(0, 20);
        
        // Mark these hotels as attempted immediately to prevent duplicate calls
        setTripAdvisorAttempted(prev => {
          const newSet = new Set(prev);
          hotelsToFetch.forEach(hotel => newSet.add(hotel.id));
          return newSet;
        });
        
        // Process hotels in smaller batches to avoid overwhelming the API
        const batchSize = 3;
        for (let i = 0; i < hotelsToFetch.length; i += batchSize) {
          const batch = hotelsToFetch.slice(i, i + batchSize);
          
          // Process batch in parallel
          const batchPromises = batch.map(async (hotel) => {

            try {
              const data = await fetchTripadvisorMatch({
                hotelId: hotel.id,
                hotelName: hotel.name,
                cityName: hotel.city?.name,
                countryName: hotel.country?.name,
                latitude: hotel.geolocation?.latitude,
                longitude: hotel.geolocation?.longitude,
                address: hotel.address,
              });
              
              if (data?.hotels?.length > 0) {
                setTripAdvisorResponses((prev) => ({ ...prev, [hotel.id]: data }));
              } else {
;
                // Store a null result to avoid refetching
                setTripAdvisorResponses((prev) => ({ ...prev, [hotel.id]: null }));
              }
            } catch (error) {
              console.error(`❌ Error fetching TripAdvisor data for ${hotel.name}:`, error);
              // Store a null result to avoid refetching on error
              setTripAdvisorResponses((prev) => ({ ...prev, [hotel.id]: null }));
            }
          });
          
          // Wait for current batch to complete
          await Promise.allSettled(batchPromises);
          
          // Add a small delay between batches to be respectful to the API
          if (i + batchSize < hotelsToFetch.length) {
            await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5 second delay
          }
        }
      };
      
      fetchTripAdvisorData();
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(tripAdvisorDebounceRef.current);
  }, [filteredHotels, visibleHotels]);

  const loadMore = () => {
    // Store current scroll position and the bottom of the current content
    const currentScrollPosition = window.pageYOffset;
    const currentContentHeight = document.documentElement.scrollHeight;
    
    setVisibleHotels((v) => v + 20);
    
    // Use requestAnimationFrame to ensure DOM has updated before adjusting scroll
    requestAnimationFrame(() => {
      // Keep user at the same visual position relative to the content they were viewing
      const newContentHeight = document.documentElement.scrollHeight;
      const contentAdded = newContentHeight - currentContentHeight;
      
      // Only adjust scroll if content was actually added and user wasn't at the very bottom
      if (contentAdded > 0 && currentScrollPosition < currentContentHeight - window.innerHeight - 100) {
        window.scrollTo({
          top: currentScrollPosition,
          behavior: 'instant' // No smooth scrolling to prevent glitches
        });
      }
    });
  };

  // Funksion për të llogaritur netët në mënyrë të saktë
  const calculateNights = (startDate, endDate) => {
    try {
      if (!startDate || !endDate) return 1;
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
      
      // Përdorim Math.max për të garantuar së paku 1 natë
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(1, diffDays);
    } catch (error) {
      console.error("Error calculating nights:", error);
      return 1;
    }
  };

  // Funksion për të marrë ikonën e duhur për facilitetin
  const getFacilityIcon = (facilityName) => {
    const iconStyle = { fontSize: 20, marginRight: 4 };
    switch(facilityName.toLowerCase()) {
      case 'wlan access':
      case 'internet access':
        return <WifiIcon style={iconStyle} />;
      case 'car park':
      case 'garage':
        return <ParkingIcon style={iconStyle} />;
      case 'restaurant(s)':
        return <RestaurantIcon style={iconStyle} />;
      case 'swimming pool':
        return <PoolIcon style={iconStyle} />;
      case 'fitness':
      case 'gym':
        return <GymIcon style={iconStyle} />;
      case 'spa':
        return <SpaIcon style={iconStyle} />;
      case 'air conditioning':
        return <AcIcon style={iconStyle} />;
      case 'room service':
        return <RoomServiceIcon style={iconStyle} />;
      case 'bar(s)':
      case 'pub(s)':
        return <BarIcon style={iconStyle} />;
      case 'conference room':
      case 'business hotel':
        return <BusinessIcon style={iconStyle} />;
      default:
        return null;
    }
  };

  // Helper function to translate facility names
  const translateFacilityName = (facilityName, translation) => {
    if (!facilityName) return facilityName;
    
    // Create a key from the facility name by converting to lowercase and replacing special characters
    const normalizedName = facilityName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/\//g, '_')
      .replace(/-/g, '_');
    
    const translationKey = `facility_${normalizedName}`;
    const translatedText = translation(translationKey);
    
    // If translation exists and is different from key, use it; otherwise use original
    return translatedText !== translationKey ? translatedText : facilityName;
  };

  // Helper function to translate theme names
  const translateThemeName = (themeName, translation) => {
    if (!themeName) return themeName;
    
    const normalizedName = themeName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/-/g, '_');
    
    const translationKey = `theme_${normalizedName}`;
    const translatedText = translation(translationKey);
    
    return translatedText !== translationKey ? translatedText : themeName;
  };

  // Helper function to translate board/meal plan names
  const translateBoardName = (boardName, translation) => {
    if (!boardName) return boardName;
    
    const normalizedName = boardName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/&/g, 'and')
      .replace(/-/g, '_');
    
    const translationKey = `board_${normalizedName}`;
    const translatedText = translation(translationKey);
    
    return translatedText !== translationKey ? translatedText : boardName;
  };

  // Funksion për të formatuar distancën
  const formatDistance = (distanceInKm, translation) => {
    if (!distanceInKm) return '';
    
    const distanceInMeters = distanceInKm * 1000;
    
    if (distanceInMeters <= 500) {
      return translation('city_center');
    } else if (distanceInMeters <= 1000) {
      return `${Math.round(distanceInMeters)}${translation('meters_from_center')}`;
    } else {
      return `${distanceInKm.toFixed(1)}${translation('km_from_center')}`;
    }
  };

  // Modifiko funksionin për të marrë facilitetet kryesore
  const getMainFacilities = (facilities) => {
    if (!facilities) return [];
    
    // Filtro vetëm facilitetet që kanë isPriced të përcaktuar dhe kanë ikonë
    const validFacilities = facilities
      .filter(f => f.isPriced !== undefined)
      .filter(f => getFacilityIcon(f.name) !== null);

    // Merr 3 facilitetet e para
    const mainFacilities = validFacilities.slice(0, 3);
    const remainingCount = validFacilities.length - 3;

    return {
      main: mainFacilities,
      remaining: remainingCount > 0 ? remainingCount : 0
    };
  };

  // Helper function to get special offer badge text
  const getSpecialOfferBadgeText = (hotel, index, specialOfferHotels) => {
    if (!specialOfferHotels.has(hotel.id)) return null;
    
    const badgeTexts = [
      translation("only_3_other_offers"),
      translation("popular_choice"),
      translation("on_demand"),
      translation("limited_time_offer")
    ];
    
    return badgeTexts[index] || translation("special_offer");
  };

  return (
    <Container isMobile={isMobile}>
      <div 
        className="d-flex justify-content-between align-items-center mb-3" 
        style={{
          padding: isMobile ? "16px" : 0,
          width: isMobile ? '100%' : 'auto',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: isMobile ? '#f5f5f5' : 'transparent'
        }}
      >
        <div className="d-block d-md-none">
          <button
            className="btn btn-light d-flex align-items-center gap-2"
            style={{ color: "#003663" }}
            onClick={() => setIsFilterOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
            </svg>
            {translation("Filters")}
          </button>
        </div>
        
        {/* Mobile Sort Button */}
        <div className="d-block d-md-none">
          <div className="position-relative" ref={mobileSortDropdownRef}>
            <button
              ref={mobileButtonRef}
              className="btn btn-light d-flex align-items-center gap-2"
              style={{ 
                color: "#003663",
                fontSize: "14px",
                padding: "8px 12px"
              }}
              onClick={handleSortButtonClick}
            >
              <SortIcon style={{ fontSize: 16 }} />
              {translation("Sort")}
              <span style={{ 
                transform: isSortDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s ease',
                fontSize: "12px"
              }}>
                ▼
              </span>
            </button>
            
            {isSortDropdownOpen && (
              <div 
                className="position-absolute bg-white rounded-lg"
                style={{ 
                  top: "calc(100% + 8px)", 
                  right: 0, 
                  zIndex: 100, 
                  minWidth: "200px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  overflow: "hidden"
                }}
              >
                {sortOptions.map((option, index) => (
                  <button
                    key={option.id}
                    className="d-block w-100 text-start btn btn-link text-decoration-none"
                    style={{ 
                      color: sortOption === option.id ? "#3B71FE" : "#003663",
                      fontWeight: sortOption === option.id ? "600" : "500",
                      padding: "12px 16px",
                      backgroundColor: sortOption === option.id ? "#F5F8FF" : "transparent",
                      transition: "all 0.2s ease",
                      borderBottom: index !== sortOptions.length - 1 ? "1px solid #E5E7EB" : "none",
                      fontSize: "14px"
                    }}
                    onMouseEnter={(e) => {
                      if (sortOption !== option.id) {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sortOption !== option.id) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                    onClick={() => {
                      setSortOption(option.id);
                      setIsSortDropdownOpen(false);
                    }}
                  >
                    {translation(option.translationKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Drawer për filtrat në mobile */}
        <DrawerComponent
          open={isFilterOpen}
          setOpen={setIsFilterOpen}
          anchor="right"
          headerText={translation("Filters")}
        >
          <div className="p-3">
            <FiltersSidebar
              allHotels={allHotels}
              filters={filters}
              onChangeFilters={setFilters}
              isMobile={isMobile}
              onCloseDrawer={() => setIsFilterOpen(false)}
              translation={translation}
            />
          </div>
        </DrawerComponent>
      </div>

      <div className={`row ${isMobile ? 'g-0 m-0 w-100' : ''}`} style={{
        width: isMobile ? '100vw' : 'auto',
        margin: 0,
        padding: 0,
        WebkitOverflowScrolling: 'touch', // Add smooth scrolling for iOS
        overscrollBehavior: 'none', // Prevent pull-to-refresh
        height: isMobile ? 'auto' : undefined
      }}>
        <aside
          className={`col-lg-3 col-md-4 d-none d-md-block`}
          style={{ paddingTop: "20px" }}
        >
          <button
            className="d-flex align-items-center gap-2 px-4 py-2 w-100 mb-3"
            style={{
              backgroundColor: "#fff",
              border: "none",
              borderRadius: 16,
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              fontWeight: 600,
              fontSize: "1rem",
              color: "#003663",
            }}
            onClick={() => setIsMapOpen(true)}
          >
            <img src={mapIcon} alt="Map" style={{ width: 32, height: 32 }} />
            {translation("show_in_map")}
          </button>
          <FiltersSidebar
            allHotels={allHotels}
            filters={filters}
            onChangeFilters={setFilters}
            isMobile={false}
            translation={translation}
          />
        </aside>

        <section 
          className={`hotel-results-section ${isMobile ? 'col-12 px-0' : 'col-lg-9 col-md-8'}`} 
          style={{
            padding: 0,
            width: isMobile ? '100%' : undefined,
            maxWidth: isMobile ? '100%' : undefined,
            margin: 0,
            backgroundColor: isMobile ? '#f5f5f5' : 'transparent',
            WebkitOverflowScrolling: 'touch', // Add smooth scrolling for iOS
            overscrollBehavior: 'none', // Prevent pull-to-refresh
            minHeight: isMobile ? '100vh' : undefined
          }}
        >
          {/* Sort dropdown - Repositioned above hotel cards */}
          <div className="d-none d-md-block mb-4">
            <div 
              className="d-flex justify-content-end align-items-center" 
              ref={desktopSortDropdownRef}
              style={{
                marginBottom: "16px",
                position: "relative"
              }}
            >
              <button
                ref={desktopButtonRef}
                className="btn d-flex align-items-center gap-2"
                style={{ 
                  color: "#003663", 
                  fontWeight: 500,
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  padding: "12px 20px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  transition: "all 0.2s ease",
                  minWidth: "180px",
                  justifyContent: "space-between"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                }}
                onClick={handleSortButtonClick}
              >
                <span className="d-flex align-items-center">
                  <SortIcon style={{ fontSize: 20, color: "#003663", marginRight: "8px" }} />
                  {translation(sortOptions.find(o => o.id === sortOption)?.translationKey || "Sort")}
                </span>
                <span style={{ 
                  transform: isSortDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▼
                </span>
              </button>
              
              {isSortDropdownOpen && (
                <div 
                  className="position-absolute bg-white rounded-lg"
                  style={{ 
                    top: "calc(100% + 8px)", 
                    right: 0, 
                    zIndex: 100, 
                    minWidth: "220px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    overflow: "hidden"
                  }}
                >
                  {sortOptions.map((option, index) => (
                    <button
                      key={option.id}
                      className="d-block w-100 text-start btn btn-link text-decoration-none"
                      style={{ 
                        color: sortOption === option.id ? "#3B71FE" : "#003663",
                        fontWeight: sortOption === option.id ? "600" : "500",
                        padding: "12px 20px",
                        backgroundColor: sortOption === option.id ? "#F5F8FF" : "transparent",
                        transition: "all 0.2s ease",
                        borderBottom: index !== sortOptions.length - 1 ? "1px solid #E5E7EB" : "none"
                      }}
                      onMouseEnter={(e) => {
                        if (sortOption !== option.id) {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (sortOption !== option.id) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                      onClick={() => {
                        setSortOption(option.id);
                        setIsSortDropdownOpen(false);
                      }}
                    >
                      {translation(option.translationKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loading && !allHotels.length && (
            <h5 style={{ color: "#003663" }}>
              {translation("finding_best_deals")}
            </h5>
          )}
          
          {priceUpdateInProgress && (
            <div style={{ 
              textAlign: "center", 
              padding: "20px", 
              color: "#003663",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              margin: "20px 0"
            }}>
              <div style={{ marginBottom: "10px" }}>
                <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#003663" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
              <div style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                {translation("updating_prices_for_selected_board_type") || "Updating prices for selected board type..."}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "4px" }}>
                Processing {hotelsProcessed}/{hotelsBeingUpdated} hotels (showing first 20 results)
              </div>
            </div>
          )}
          
          {!loading && !filteredHotels.length && <NoResultsFoundWithButtons />}
          
          {/* Show info when meal plan filter is applied and there are more than 20 hotels */}
          {!loading && filteredHotels.length > 20 && filters.mealPlan && filters.mealPlan.length > 0 && (
            <div style={{ 
              textAlign: "center", 
              padding: "12px", 
              color: "#666",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              margin: "20px 0",
              fontSize: "0.85rem"
            }}>
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Showing first 20 hotels with updated prices
              </div>
              <div style={{ fontSize: "0.8rem" }}>
                {filteredHotels.length - 20} more hotels available with {filters.mealPlan.join(', ')} board type
              </div>
            </div>
          )}

          {filteredHotels.slice(0, visibleHotels).map((hotel, idx) => {
            const nights = calculateNights(
              effectiveParams.checkInDate,
              effectiveParams.checkOutDate
            );
            
            // Get the correct price (updated or original)
            const getDisplayPrice = (hotel) => {
              if (updatedPrices[hotel.id]) {
                return updatedPrices[hotel.id].price;
              }
              return hotel.displayPrice;
            };
            
            const displayPrice = getDisplayPrice(hotel);
            const [intPart, decimalPart] = displayPrice
              .toFixed(2)
              .split(".");
            const hasOffer = hotel.badges?.some((b) => b.id === "specialoffer");
            const isSpecialPriceOffer = specialOfferHotels.has(hotel.id);
            const ta = tripAdvisorResponses[hotel.id]?.hotels?.[0] || {};

            const rawScore = ta.averageRating
              ? ta.averageRating * 20
              : (hotel.rating || 0) * 20;
            const score = Math.min(Math.round(rawScore), 100);

            const label = getScoreLabel(score, translation);
            const count = ta.reviewCount ? `(${ta.reviewCount})` : "";
            
            const facilities = getMainFacilities(hotel.facilities);
            
            // Find the index of this hotel in the special offers for proper badge text
            let specialOfferIndex = 0;
            if (isSpecialPriceOffer) {
              const specialOfferIds = Array.from(specialOfferHotels);
              specialOfferIndex = specialOfferIds.indexOf(hotel.id);
            }

            return (
              <HotelCard
                key={hotel.id + idx}
                isMobile={isMobile}
                onClick={() => {
                  if (!hotel.id || hotel.id === "unknown") {
                    console.error('Invalid hotel ID:', hotel.id);
                    return;
                  }
                  navigate(`/hotels/details/${hotel.id}`, {
                    state: {
                      hotelId: hotel.id,
                      hotelSearchData: hotel,
                      searchId,
                      searchExpiresOn, // ✅ Pass search expiration to hotel details
                      offerId: hotel.offers?.[0]?.offerId,
                      checkInDate: effectiveParams.checkInDate,
                      checkOutDate: effectiveParams.checkOutDate,
                      address: hotel.address,
                      nationality: effectiveParams.nationality,
                      urlParams: window.location.search
                    },
                  });
                }}
              >
                <HotelImage isMobile={isMobile}>
                  {/* Special Price Offer Badge - Higher priority */}
                  {isSpecialPriceOffer && (
                    <SpecialOfferBadge isMobile={isMobile}>
                      {getSpecialOfferBadgeText(hotel, specialOfferIndex, specialOfferHotels)}
                    </SpecialOfferBadge>
                  )}
                  
                  {/* Regular Special Offer Badge - Lower priority */}
                  {hasOffer && !isSpecialPriceOffer && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: isMobile ? 100 : 140,
                        left: 8,
                        backgroundColor: "#FBD316",
                        color: "#003663",
                        padding: "6px 12px",
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        zIndex: 2,
                      }}
                    >
                      {translation("special_offer")}
                    </div>
                  )}
                  <img
                    src={hotel.thumbnailFull}
                    alt={hotel.name}
                  />
                </HotelImage>

                <div className="flex-grow-1 p-3 d-flex flex-column justify-content-between">
                  <div>
                    <div className="mb-2" style={{ 
                      marginTop: isMobile ? "8px" : 0 
                    }}>
                      {Array.from({ length: Math.round(hotel.stars || 0) }).map(
                        (_, i) => (
                          <img
                            key={i}
                            src={starIcon}
                            alt="star"
                            style={{
                              width: isMobile ? 16 : 18,
                              height: isMobile ? 16 : 18,
                              marginRight: isMobile ? 2 : 4,
                            }}
                          />
                        )
                      )}
                    </div>

                    <h5 style={{ 
                      fontWeight: "bold", 
                      marginBottom: isMobile ? "8px" : "0.25rem",
                      fontSize: isMobile ? "1.1rem" : "1.25rem",
                      lineHeight: isMobile ? "1.3" : "inherit"
                    }}>
                      {hotel.name}
                    </h5>

                    {/* Room Type */}
                    {(updatedPrices[hotel.id]?.roomName || hotel.offers?.[0]?.rooms?.[0]?.roomName) && (
                      <p className="d-flex align-items-center" style={{ 
                        fontSize: isMobile ? "0.85rem" : "0.85rem", 
                        color: "#003663", 
                        margin: isMobile ? "0 0 8px" : "0.25rem 0",
                        fontWeight: "600"
                      }}>
                        <BedIcon style={{ 
                          fontSize: isMobile ? "16px" : "18px", 
                          marginRight: "6px",
                          color: "#003663"
                        }} />
                        {updatedPrices[hotel.id]?.roomName || hotel.offers[0].rooms[0].roomName}
                      </p>
                    )}

                    {/* Meal Type */}
                    {(updatedPrices[hotel.id]?.boardName || hotel.offers?.[0]?.rooms?.[0]?.boardName) && (
                      <p className="d-flex align-items-center" style={{ 
                        fontSize: isMobile ? "0.85rem" : "0.85rem", 
                        color: "#003663", 
                        margin: isMobile ? "0 0 12px" : "0.25rem 0",
                        fontWeight: "500"
                      }}>
                        <CutlerySVG style={{ 
                          width: isMobile ? "16px" : "18px",
                          height: isMobile ? "16px" : "18px", 
                          marginRight: "6px",
                          fill: "#003663"
                        }} />
                        {getMealTypeDisplay(updatedPrices[hotel.id]?.boardName || hotel.offers[0].rooms[0].boardName, translation)}
                      </p>
                    )}

                    <div className="d-flex flex-column gap-2" style={{
                      marginBottom: isMobile ? "8px" : "8px"
                    }}>
                      <div className="d-flex align-items-center" style={{ 
                        fontSize: isMobile ? "0.85rem" : "0.85rem", 
                        color: "#003663",
                        marginBottom: 0,
                        fontWeight: "500"
                      }}>
                        <span className="d-flex align-items-center">
                          <DarkMoonIcon style={{ 
                            marginRight: isMobile ? "8px" : "10px", 
                            fontSize: isMobile ? "14px" : "14px" 
                          }} />
                          <span style={{ marginLeft: isMobile ? "2px" : "10px" }}>
                            {" "}{nights} {nights === 1 ? translation("night") : translation("nights")}
                          </span>
                        </span>
                        <span style={{ margin: "0 8px" }}>|</span>
                        <span className="d-flex align-items-center">
                          <PersonOutlineIcon style={{ 
                            fontSize: isMobile ? "18px" : "20px", 
                            marginRight: "4px" 
                          }} />
                          <span>
                            {effectiveParams.adults} {effectiveParams.adults === 1 ? translation("adult") : translation("adults")}
                            {effectiveParams.children > 0 && `, ${effectiveParams.children} ${effectiveParams.children === 1 ? translation("child") : translation("children")}`}
                          </span>
                        </span>
                      </div>
                    </div>

                    {hotel.distanceFromCenter && (
                      <p className="mb-2" style={{ 
                        fontSize: isMobile ? "0.85rem" : "0.85rem", 
                        color: "#003663", 
                        marginBottom: isMobile ? "8px" : "8px",
                        marginTop: 0,
                        fontWeight: "500"
                      }}>
                        <LocationIcon style={{ 
                          fontSize: isMobile ? 16 : 16, 
                          marginRight: 8, 
                          verticalAlign: "text-bottom" 
                        }} />
                        {formatDistance(hotel.distanceFromCenter, translation)}
                      </p>
                    )}

                    {/* Facilities with remaining count - show on both mobile and desktop */}
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {facilities.main.map((facility, idx) => {
                        const icon = getFacilityIcon(facility.name);
                        return (
                          <div
                            key={idx}
                            className="d-flex align-items-center"
                            style={{
                              backgroundColor: "#f5f5f5",
                              padding: isMobile ? "3px 6px" : "4px 8px",
                              borderRadius: "4px",
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              color: "#003663"
                            }}
                          >
                            {icon && React.cloneElement(icon, {
                              style: {
                                ...icon.props.style,
                                fontSize: isMobile ? 16 : 20,
                                marginRight: isMobile ? 3 : 4
                              }
                            })}
                            <span>{translateFacilityName(facility.name, translation)}</span>
                          </div>
                        );
                      })}
                      {facilities.remaining > 0 && (
                        <div
                          className="d-flex align-items-center"
                          style={{
                            backgroundColor: "#f5f5f5",
                            padding: isMobile ? "3px 6px" : "4px 8px",
                            borderRadius: "4px",
                            fontSize: isMobile ? "0.7rem" : "0.75rem",
                            color: "#003663",
                            opacity: 0.8
                          }}
                        >
                          +{facilities.remaining} {translation("more")}
                        </div>
                      )}
                    </div>

                    {/* Hotel Themes Badges - show on both mobile and desktop */}
                    {hotel.themes && hotel.themes.length > 0 && (
                      <div className="d-flex flex-wrap gap-2" style={{ 
                        marginBottom: isMobile ? "8px" : "4px",
                        marginTop: isMobile ? "8px" : "0"
                      }}>
                        {hotel.themes.slice(0, 3).map((theme, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center"
                            style={{
                              backgroundColor: "#E8F0FE",
                              padding: "2px 10px",
                              borderRadius: "20px",
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              color: "#3B71FE",
                              fontWeight: "500",
                              border: "1px solid #D1E3FF"
                            }}
                          >
                            {translateThemeName(theme.name, translation)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-end" style={{ 
                    marginTop: isMobile ? "8px" : "-80px" 
                  }}>
                    <div className="d-flex align-items-center" style={{ 
                      marginTop: isMobile ? "0" : "-65px" 
                    }}>
                      <div 
                        style={{ 
                          backgroundColor: "#4CAF50",
                          color: "white",
                          padding: isMobile ? "2px 8px" : "4px 12px",
                          borderRadius: "4px",
                          fontSize: isMobile ? "0.9rem" : "1.1rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          marginRight: "8px"
                        }}
                      >
                        {score}
                      </div>
                      <div style={{ 
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        fontWeight: "500",
                        color: "#003663"
                      }}>
                        {label}
                      </div>
                    </div>

                    <div className="text-end" style={{ 
                      marginTop: isMobile ? "0" : "-65px" 
                    }}>
                      <div style={{ 
                        fontSize: isMobile ? "0.75rem" : "0.85rem", 
                        color: "#003663", 
                        marginBottom: "2px",
                        letterSpacing: "0.2px",
                        fontWeight: "500",
                        marginRight: "5px"
                      }}>
                        {translation("room_price")}
                        <br />
                        {translation("starting_from")}
                      </div>
                      <div 
                       onMouseEnter={() => setHoveredPriceId(hotel.id)}
                       onMouseLeave={() => setHoveredPriceId(null)}
                      style={{ 
                        fontSize: isMobile ? "1.2rem" : "1.5rem", 
                        fontWeight: "500",
                        color: "#003663", 
                        lineHeight: "1",
                        transform: hoveredPriceId === hotel.id ? "scale(1.10)" : "scale(1)",
                        transition: "transform 0.2s ease-in-out",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "flex-end",
                        backgroundColor: "#FBD215",
                        padding: isMobile ? "8px 12px" : "10px 16px",
                        borderRadius: "32px",
                        boxShadow: "rgba(0, 0, 0, 0.25) 0px 6px 12px"
                      }}>
                        {intPart}
                        <span style={{ 
                          fontSize: isMobile ? "0.85rem" : "1rem",
                          marginLeft: "2px",
                          fontWeight: "600",
                          color: "#003663"
                        }}>
                          .{decimalPart}
                        </span>
                        <span style={{
                          marginLeft: "4px",
                          fontSize: isMobile ? "1rem" : "1.2rem",
                          color: "#003663",
                          fontWeight: "600"
                        }}>
                          {getCurrencySymbol(effectiveParams.currency)}
                        </span>
                      </div>
                      
                      {/* Show indicator when price has been updated for meal plan filter */}
                      {updatedPrices[hotel.id] && (
                        <div style={{
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          color: "#4CAF50",
                          fontWeight: "600",
                          textAlign: "center",
                          marginTop: "4px",
                          backgroundColor: "#E8F5E8",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          border: "1px solid #C8E6C9"
                        }}>
                          {updatedPrices[hotel.id].boardName}
                        </div>
                      )}
                      <div style={{ 
                        fontSize: isMobile ? "0.75rem" : "0.85rem", 
                        color: "#003663",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "4px",
                        fontWeight: "500",
                        backgroundColor: "#f5f5f5",
                        padding: isMobile ? "2px 6px" : "4px 8px",
                        borderRadius: "4px"
                      }}>
                        <WalletIcon style={{ 
                          fontSize: isMobile ? 12 : 14, 
                          color: "#003663" 
                        }} />
                        {translation("pay_online")}
                      </div>
                    </div>
                  </div>
                </div>
              </HotelCard>
            );
          })}

          {filteredHotels.length > visibleHotels && (
            <div className="d-flex justify-content-center" style={{
              padding: "16px",
              position: "relative",
              marginTop: "16px",
              marginBottom: "32px"
            }}>
              <button
                className="btn btn-outline-primary"
                onClick={loadMore}
                style={{
                  borderColor: "#3B71FE",
                  color: "#3B71FE",
                  fontWeight: 500,
                  padding: "12px 32px",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  width: isMobile ? "calc(100% - 32px)" : "auto",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
              >
                {translation("load_more")}
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Map Modal */}
      <HotelMapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        hotels={filteredHotels}
        cityLat={effectiveParams.cityLat}
        cityLng={effectiveParams.cityLng}
      />
    </Container>
  );
};

export default HotelSearchResults;
